import React, { useState } from 'react';
import { BookOpen, Users, Calendar, Award, FileText, TrendingUp, Shield, ChevronDown, ChevronUp, GraduationCap, Globe, Calculator, Beaker, DollarSign, MapPin, User } from 'lucide-react';

const SchoolCurriculum: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<number | null>(null);

  const curriculumSections = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "CBSE Affiliation & Medium",
      color: "from-blue-600 to-indigo-600",
      bgColor: "from-blue-50 to-indigo-50",
      content: [
        "School follows the syllabus in strict accordance to C.B.S.E., New Delhi",
        "Medium of instruction is English",
        "Hindi is taught as compulsory subject up to class X",
        "AI, IT and Sanskrit are taught as sixth subject"
      ]
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Stream Options & Subjects",
      color: "from-purple-600 to-pink-600",
      bgColor: "from-purple-50 to-pink-50",
      content: [
        "Senior Secondary School with Science, Commerce and Humanities groups",
        "Science Stream: English, Physics, Chemistry as compulsory subjects",
        "Commerce Stream: English, Accountancy, Business Studies, Economics as compulsory",
        "Humanities Stream: English, History, Political Science as compulsory",
        "Optional subjects: Biology/Mathematics and Physical Education/Hindi/Computer Science/I.P."
      ]
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Academic Structure",
      color: "from-green-600 to-emerald-600",
      bgColor: "from-green-50 to-emerald-50",
      content: [
        "Latest pattern of CBSE is strictly followed from class I to XII",
        "Academic session divided into two terms",
        "Term-I: April to September",
        "Term-II: October to March"
      ]
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Assessment Methods",
      color: "from-orange-600 to-red-600",
      bgColor: "from-orange-50 to-red-50",
      content: [
        "Variety of assessment tasks: pen-paper tests, debates, GD, seminars, practicals, oral examinations, speeches",
        "Best of two or three performances considered for Formative assessment",
        "One Summative Examination at the end of each term",
        "Periodic progress reports shared with parents throughout the year"
      ]
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Promotion Criteria",
      color: "from-cyan-600 to-blue-600",
      bgColor: "from-cyan-50 to-blue-50",
      content: [
        "Promotion based on CBSE weightage for Formatives & Summatives",
        "Qualifying marks: 33% in each subject for Summative Assessment",
        "For subjects with practical work: 33% in theory + 33% in practical + 33% aggregate",
        "School authorities' decision on promotion is final and binding"
      ]
    }
  ];

  const streamSubjects = [
    {
      stream: "Science",
      icon: <Beaker className="w-5 h-5" />,
      color: "from-blue-500 to-cyan-500",
      compulsory: ["English", "Physics", "Chemistry"],
      optional: ["Biology", "Mathematics", "Physical Education", "Hindi", "Computer Science", "I.P."]
    },
    {
      stream: "Commerce",
      icon: <DollarSign className="w-5 h-5" />,
      color: "from-green-500 to-emerald-500",
      compulsory: ["English", "Accountancy", "Business Studies", "Economics"],
      optional: ["Mathematics", "Physical Education", "Hindi", "Computer Science", "I.P."]
    },
    {
      stream: "Humanities",
      icon: <Globe className="w-5 h-5" />,
      color: "from-purple-500 to-pink-500",
      compulsory: ["English", "History", "Political Science"],
      optional: ["Physical Education", "Hindi", "Computer Science", "I.P.", "Additional subjects"]
    }
  ];

  const toggleSection = (index: number) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full mb-6 shadow-xl">
            <BookOpen className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            School Curriculum
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive educational framework following CBSE guidelines with modern teaching methodologies
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Stream Overview Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {streamSubjects.map((stream, index) => (
            <div key={index} className="group hover:scale-105 transition-all duration-500">
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/30 h-full relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${stream.color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                <div className="relative z-10">
                  <div className={`w-12 h-12 bg-gradient-to-r ${stream.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                    <div className="text-white">{stream.icon}</div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">{stream.stream} Stream</h3>
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Compulsory Subjects:</h4>
                    <div className="flex flex-wrap gap-2">
                      {stream.compulsory.map((subject, idx) => (
                        <span key={idx} className={`px-3 py-1 bg-gradient-to-r ${stream.color} text-white text-sm rounded-full shadow-sm`}>
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Optional Subjects:</h4>
                    <div className="flex flex-wrap gap-2">
                      {stream.optional.slice(0, 3).map((subject, idx) => (
                        <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {subject}
                        </span>
                      ))}
                      {stream.optional.length > 3 && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          +{stream.optional.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Curriculum Sections */}
        <div className="space-y-6">
          {curriculumSections.map((section, index) => (
            <div key={index} className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 overflow-hidden">
              <div 
                className={`bg-gradient-to-r ${section.bgColor} p-6 cursor-pointer hover:shadow-lg transition-all duration-300`}
                onClick={() => toggleSection(index)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${section.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <div className="text-white">{section.icon}</div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">{section.title}</h3>
                  </div>
                  <div className="text-gray-600">
                    {expandedSection === index ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
                  </div>
                </div>
              </div>
              
              {expandedSection === index && (
                <div className="p-6 bg-white/50 animate-fade-in">
                  <div className="space-y-4">
                    {section.content.map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className={`w-2 h-2 bg-gradient-to-r ${section.color} rounded-full mt-2 flex-shrink-0`}></div>
                        <p className="text-gray-700 leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Key Highlights */}
        <div className="mt-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-8 text-white shadow-2xl">
          <h3 className="text-3xl font-bold mb-6 text-center">Assessment Highlights</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8" />
              </div>
              <h4 className="font-semibold mb-2">33% Qualifying</h4>
              <p className="text-sm opacity-90">Minimum marks required in each subject</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8" />
              </div>
              <h4 className="font-semibold mb-2">Two Terms</h4>
              <p className="text-sm opacity-90">Academic year divided into two terms</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8" />
              </div>
              <h4 className="font-semibold mb-2">Continuous Assessment</h4>
              <p className="text-sm opacity-90">Regular evaluation through various methods</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h4 className="font-semibold mb-2">CBSE Standards</h4>
              <p className="text-sm opacity-90">Strict adherence to CBSE guidelines</p>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SchoolCurriculum;